/* ========================================= */
/* src/pages/CourseDetail.tsx - DETALLE DE CAPACITACIÓN */
/* ========================================= */

import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Users, Calendar, CheckCircle, User, Mail } from 'lucide-react';
import { courses } from '../data/courses';
import { parseLocalDate, getVideoEmbedUrl } from '../lib/utils';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import Reveal from '../components/ui/Reveal';

const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dni: '',
    company: ''
  });

  const course = courses.find(c => c.id === id);

  const handleWhatsAppContact = () => {
    const phoneNumber = '51921259616'; // Tu número de WhatsApp (sin signos + ni espacios)
    const message = `Hola soy ${formData.name} 👋

  Estoy interesado en inscribirme al *${course?.title}*.
  *${course?.description}*.
  Mi numero de Telefono es: *${formData.phone}*.
  Mi DNI es: *${formData.dni}*.
  Mi Correo es: *${formData.email}*.

  ¡Estaré atento, gracias!`;

    // URL para WhatsApp Web y móvil
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

    // Abrir en nueva ventana/pestaña
    window.open(whatsappUrl, '_blank');
  };

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Capacitación no encontrada</h1>
          <Link to="/capacitaciones" className="btn-primary">
            Volver a capacitaciones
          </Link>
        </div>
      </div>
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleWhatsAppContact();
    setShowRegistrationForm(false);
  };

  return (
    <div className="min-h-screen bg-infinity-black">
      {/* Hero Section */}
      <section className="bg-infinity-gradient text-infinity-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Link
            to="/capacitaciones"
            className="inline-flex items-center text-infinity-white hover:text-infinity-yellow hover:-translate-x-1 transition-all mb-6"
          >
            <ArrowLeft size={20} className="mr-2" />
            Volver a capacitaciones
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
              <span className="bg-infinity-yellow text-infinity-black px-3 py-1 text-sm font-medium rounded-full">
                {course.level.charAt(0).toUpperCase() + course.level.slice(1)}
              </span>
              <h1 className="text-4xl font-bold mt-4 mb-4 text-infinity-yellow">{course.title}</h1>
              <p className="text-xl text-infinity-white/80">{course.description}</p>
            </div>

            <div
              className="glass rounded-lg p-6 opacity-0 animate-fade-in-up"
              style={{ animationDelay: '250ms' }}
            >
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Clock size={20} className="text-infinity-yellow" />
                  <div>
                    <p className="font-medium">Duración</p>
                    <p className="text-gray-300">{course.duration}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Users size={20} className="text-infinity-yellow" />
                  <div>
                    <p className="font-medium">Modalidad</p>
                    <p className="text-gray-300 capitalize">{course.modality}</p>
                  </div>
                </div>

                {course.startDate && (
                  <div className="flex items-center space-x-3">
                    <Calendar size={20} className="text-infinity-yellow" />
                    <div>
                      <p className="font-medium">Fecha de inicio</p>
                      <p className="text-gray-300">{parseLocalDate(course.startDate).toLocaleDateString('es-PE')}</p>
                    </div>
                  </div>
                )}

                {course.schedule && (
                  <div className="flex items-center space-x-3">
                    <Clock size={20} className="text-infinity-yellow" />
                    <div>
                      <p className="font-medium">Horario</p>
                      <p className="text-gray-300 text-sm">{course.schedule}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contenido Principal */}
          <div className="lg:col-span-2 space-y-8">
            {/* Descripción */}
            <Reveal className="bg-infinity-dark rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-infinity-yellow mb-4">Descripción General</h2>
              <p className="text-infinity-white/80 leading-relaxed">{course.description}</p>
            </Reveal>

            {/* Temario */}
            <Reveal delay={100} className="bg-infinity-dark rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-infinity-yellow mb-4">Temario por Módulos</h2>
              <Accordion type="single" collapsible defaultValue="module-0" className="space-y-2">
                {course.modules.map((module, index) => (
                  <AccordionItem
                    key={index}
                    value={`module-${index}`}
                    className="border border-white/10 rounded-lg px-4 hover:border-infinity-yellow/40 transition-colors"
                  >
                    <AccordionTrigger className="text-left hover:no-underline">
                      <div className="flex justify-between items-center w-full pr-4">
                        <h3 className="text-lg font-semibold text-infinity-yellow">
                          Módulo {index + 1}: {module.title}
                        </h3>
                        <span className="text-sm text-gray-400 ml-4 shrink-0">{module.duration}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-1">
                        {module.topics.map((topic, topicIndex) => (
                          <li key={topicIndex} className="flex items-center space-x-2 text-infinity-white/80">
                            <CheckCircle size={16} className="text-green-500 shrink-0" />
                            <span>{topic}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>

            {/* Requisitos */}
            <Reveal delay={150} className="bg-infinity-dark rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-infinity-yellow mb-4">Requisitos</h2>
              <ul className="space-y-2">
                {course.requirements.map((requirement, index) => (
                  <li key={index} className="flex items-center space-x-2">
                    <CheckCircle size={16} className="text-green-500" />
                    <span className="text-infinity-white">{requirement}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Precio e Inscripción */}
            <div className="bg-infinity-dark rounded-lg shadow-lg p-6 sticky top-24 card-hover border border-transparent hover:border-infinity-yellow/30">
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-infinity-yellow mb-2">
                  Costo: {course.price.costo.gen}
                </div>
                <p className="text-infinity-white/80">Egresados/Trabajadores en General: {course.price.costo.gen}</p>
                <p className="text-infinity-white/80">Con Carnet Universitario: {course.price.costo.uni}</p>
              </div>

              {course.startDate && parseLocalDate(course.startDate) < new Date() ? (
                <button
                  disabled
                  className="w-full btn-primary bg-gray-500 text-infinity-white text-lg py-4 mb-4 cursor-not-allowed opacity-70"
                >
                  Inscripciones Cerradas
                </button>
              ) : (
                <button
                  onClick={() => setShowRegistrationForm(true)}
                  className="w-full btn-primary bg-infinity-yellow text-infinity-black text-lg py-4 mb-4 hover:bg-infinity-purple hover:text-infinity-white hover:scale-[1.02] hover:shadow-glow transition-all duration-300"
                >
                  Inscríbete Ahora
                </button>
              )}

              <div className="text-center">
                <p className="text-sm text-infinity-white/80">
                  ✓ Certificado oficial incluido<br />
                  ✓ Material didáctico
                </p>
              </div>
            </div>

            {/* Información del Instructor */}
            <Reveal className="bg-infinity-dark rounded-lg shadow-lg p-6 card-hover">
              <h3 className="text-lg font-semibold text-infinity-yellow mb-4">{course.docente.at(0)?.name}</h3>
              <div className="text-center">
                <div className="w-48 h-48 mx-auto mb-3 rounded-full overflow-hidden">
                  <img
                    src={course.docente.at(0)?.photo_url}
                    alt={course.docente.at(0)?.name}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <h4 className="font-medium text-infinity-yellow">{course.docente.at(0)?.proffession}{course.docente.at(0)?.sigUniv}</h4>
                <p className="text-sm text-infinity-white/80">{course.docente.at(0)?.description}</p>
              </div>
            </Reveal>

            {/* Video del Curso */}
            {course.videoUrl && getVideoEmbedUrl(course.videoUrl) && (
              <Reveal className="bg-infinity-dark rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold text-infinity-yellow mb-4"></h3>
                <div className="aspect-[9/16] w-full rounded-lg overflow-hidden">
                  <iframe
                    src={getVideoEmbedUrl(course.videoUrl)!}
                    title={`Video de presentación: ${course.title}`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Inscripción */}
      {showRegistrationForm && (
        <div className="fixed inset-0 bg-infinity-black bg-opacity-80 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-infinity-dark rounded-lg max-w-md w-full p-6 animate-scale-in">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-infinity-yellow">Inscripción</h3>
              <button
                onClick={() => setShowRegistrationForm(false)}
                className="text-infinity-yellow hover:text-infinity-white hover:rotate-90 transition-all duration-300"
              >
                ×
              </button>
            </div>

            <p className="text-infinity-white/80 mb-4">
              Completa el formulario para inscribirte en: <strong>{course.title}</strong>
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-infinity-yellow mb-1">
                  Nombre completo *
                </label>
                <div className="relative">
                  <User size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-infinity-yellow" />
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white transition-shadow"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-infinity-yellow mb-1">
                  Email *
                </label>
                <div className="relative">
                  <Mail size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-infinity-yellow" />
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white transition-shadow"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-infinity-yellow mb-1">
                  Teléfono
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white transition-shadow"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-infinity-yellow mb-1">
                  DNI *
                </label>
                <input
                  type="text"
                  name="dni"
                  required
                  value={formData.dni}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white transition-shadow"
                />
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowRegistrationForm(false)}
                  className="flex-1 btn-secondary bg-infinity-dark text-infinity-yellow hover:bg-infinity-yellow hover:text-infinity-black transition-all duration-300"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary bg-infinity-yellow text-infinity-black hover:bg-infinity-purple hover:text-infinity-white hover:scale-105 transition-all duration-300"
                >
                  Enviar Inscripción
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetail;
